import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import AddAppModal from '../AddAppModal';

function AppSelectionPage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [userManagementDialogOpen, setUserManagementDialogOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [addAppModalOpen, setAddAppModalOpen] = useState(false);
  
  const navigate = useNavigate();
  const { 
    currentUser, 
    token, 
    isFullAdmin, 
    isRegularAdmin, 
    updateProfile, 
    logout 
  } = useAuth();

  const fetchApps = useCallback(async () => {
    if (!currentUser || !token) return; 

    setLoading(true);
    setError(null);
    try {
      console.log('[AppSelectionPage] Fetching apps with token...');
      const response = await axios.get('/api/apps', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('[AppSelectionPage] Apps fetched:', response.data);
      setApps(response.data);
    } catch (err) {
      console.error('[AppSelectionPage] Error fetching apps:', err);
      setError(err.response?.data?.message || 'Failed to load applications. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, token]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  // Auto-redirect org_admin to their only app
  useEffect(() => {
    if (currentUser?.role === 'org_admin' && apps.length === 1) {
      navigate(`/app/${apps[0].id}`, { replace: true });
    }
  }, [currentUser, apps, navigate]);

  const handleAppSelect = (appId) => {
    navigate(`/app/${appId}`);
  };

  const handleAddNewApp = () => {
    setAddAppModalOpen(true);
  };

  const handleAddAppModalClose = () => {
    setAddAppModalOpen(false);
  };
  
  const handleAppAdded = () => {
    handleAddAppModalClose();
    fetchApps();
  };

  const handleProfileDialogOpen = () => {
    setUsername(currentUser?.username || '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setProfileError('');
    setProfileSuccess('');
    setProfileDialogOpen(true);
  };

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
  };

  const handleUserManagementDialogOpen = async () => {
    if (isFullAdmin()) {
      setAdminLoading(true);
      try {
        const response = await axios.get('/api/admins'); 
        setAdminUsers(response.data);
      } catch (err) {
        console.error('Error fetching admin users:', err);
        setError('Failed to load admin users');
      } finally {
        setAdminLoading(false);
      }
      setUserManagementDialogOpen(true);
    }
  };

  const handleUserManagementDialogClose = () => {
    setUserManagementDialogOpen(false);
  };

  const handleProfileUpdate = async () => {
    setProfileError('');
    setProfileSuccess('');
    
    if (newPassword && newPassword !== confirmPassword) {
      setProfileError("New passwords don't match");
      return;
    }
    
    const updateData = {};
    if (username !== currentUser.username) {
      updateData.username = username;
    }
    
    if (newPassword) {
      if (!currentPassword) {
        setProfileError("Current password is required");
        return;
      }
      updateData.currentPassword = currentPassword;
      updateData.newPassword = newPassword;
    }
    
    if (Object.keys(updateData).length === 0) {
      setProfileError("No changes to update");
      return;
    }
    
    try {
      const success = await updateProfile(updateData);
      if (success) {
        setProfileSuccess('Profile updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
         // Error is likely set within updateProfile context function
         // If not, uncomment below:
         // setProfileError('Failed to update profile');
      }
    } catch (err) {
      setProfileError(err.message || 'An unexpected error occurred during profile update.');
    }
  };

  const handleAddAdminUser = () => {
    navigate('/admins/new');
  };

  const handleEditAdminUser = (adminId) => {
    navigate(`/admins/${adminId}`);
  };

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginY: 4
        }}
      >
        <Typography variant="h4" component="h1">
          Select Application
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> 
          <IconButton 
            color="primary" 
            onClick={handleProfileDialogOpen}
            title="User Settings"
          >
            <AccountCircleIcon />
          </IconButton>
          
          {isFullAdmin() && (
            <IconButton 
              color="primary" 
              onClick={handleUserManagementDialogOpen}
              title="User Management"
            >
              <PeopleIcon />
            </IconButton>
          )}
          
          {isFullAdmin() && (
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleAddNewApp}
            >
              ADD NEW APP
            </Button>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginY: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ marginY: 2 }}>
          {error}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {apps.map((app) => (
            <Card
              key={app.id}
              sx={{
                width: 300,
                cursor: 'pointer',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: 6
                }
              }}
              onClick={() => handleAppSelect(app.id)}
            >
              <CardContent>
                <Typography variant="h5" component="div" gutterBottom>
                  {app.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ID: {app.id}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <AddAppModal 
        open={addAppModalOpen} 
        onClose={handleAddAppModalClose} 
        onAppAdded={handleAppAdded} 
      />

      <Dialog open={profileDialogOpen} onClose={handleProfileDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>User Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ marginY: 2 }}>
            <TextField
              label="Username"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
            />
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Change Password (leave blank to keep current)
            </Typography>
            <TextField
              label="Current Password"
              type="password"
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
            />
            <TextField
              label="New Password"
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
            />
            
            {profileError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {profileError}
              </Typography>
            )}
            
            {profileSuccess && (
              <Typography color="success.main" sx={{ mt: 2 }}>
                {profileSuccess}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProfileDialogClose}>Cancel</Button>
          <Button onClick={handleProfileUpdate} variant="contained" color="primary">
            Save Changes
          </Button>
          <Button onClick={logout} color="error" sx={{ marginLeft: 'auto' }}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={userManagementDialogOpen} onClose={handleUserManagementDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Admin User Management</Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={handleAddAdminUser}
            >
              Add User
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {adminLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ width: '100%' }}>
              {adminUsers.map((admin, index) => (
                <React.Fragment key={admin.id}>
                  <ListItem
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleEditAdminUser(admin.id)}>
                        <SettingsIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={admin.username}
                      secondary={`Role: ${admin.role === 'full_admin' ? 'Full Admin' : admin.role === 'redaktion' ? 'Redaktion' : admin.role === 'org_admin' ? 'Org Admin' : 'Regular Admin'}`}
                    />
                  </ListItem>
                  {index < adminUsers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUserManagementDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AppSelectionPage;